import React, { useState, useEffect } from 'react';
import { submitFeedback } from '../services/feedback.service';
import { useBrandedImages } from '../hooks/useBranding';

const styles = {
  pageBg: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f4f7fb 0%, #e9eef6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(44,62,80,0.10)',
    padding: '2.5em 2em 2em 2em',
    fontFamily: 'Arial, Helvetica, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    display: 'block',
    margin: '0 auto 1.5em auto',
    maxWidth: 180, // was 100
    height: 90,    // was 60
    objectFit: 'contain',
  },
  h1: {
    color: '#0c3b93',
    textAlign: 'center',
    marginBottom: '1.5em',
    fontSize: '1.5em',
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  stars: {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'center',
    marginBottom: '1.5em',
    gap: 4,
  },
  star: {
    fontSize: '2.3em',
    color: '#b0b8c9',
    cursor: 'pointer',
    transition: 'color 0.2s',
    outline: 'none',
    border: 'none',
    background: 'none',
    padding: 0,
    lineHeight: 1,
  },
  starSelected: {
    color: '#f5b301',
  },
  textarea: {
    width: '100%',
    minHeight: 90,
    borderRadius: 8,
    border: '1px solid #d1d7e6',
    padding: '1em',
    fontSize: '1em',
    marginBottom: '1.5em',
    resize: 'vertical',
    background: '#f8fafc',
    color: '#222',
    outline: 'none',
    transition: 'border 0.2s',
  },
  textareaFocus: {
    border: '1.5px solid #0c3b93',
  },
  btn: {
    background: 'linear-gradient(90deg, #0c3b93 0%, #2563eb 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0.9em 0',
    fontSize: '1.08em',
    fontWeight: 700,
    cursor: 'pointer',
    width: '100%',
    marginTop: 4,
    boxShadow: '0 2px 8px rgba(44,62,80,0.08)',
    letterSpacing: 0.5,
    transition: 'background 0.2s',
  },
  btnDisabled: {
    background: '#b0b8c9',
    color: '#fff',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  thankyou: {
    textAlign: 'center',
    color: '#0c3b93',
    fontSize: '1.25em',
    marginTop: '2em',
    fontWeight: 600,
  },
  error: {
    color: '#c00',
    textAlign: 'center',
    marginBottom: '1em',
    fontSize: '1em',
  },
  loading: {
    textAlign: 'center',
    color: '#0c3b93',
    marginBottom: '1em',
    fontSize: '1em',
  },
  label: {
    display: 'block',
    marginBottom: '0.7em',
    color: '#333',
    fontWeight: 600,
    fontSize: '1em',
    textAlign: 'left',
    width: '100%',
  },
};

function getServiceIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('service_id');
}

const ShareFeedback = ({ serviceId: propServiceId }) => {
  const [selectedRating, setSelectedRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [thankYou, setThankYou] = useState(false);
  const [serviceId, setServiceId] = useState(propServiceId || '');
  const [textareaFocus, setTextareaFocus] = useState(false);
  
  // Get branded logo
  const { logo } = useBrandedImages();

  useEffect(() => {
    if (!propServiceId) {
      setServiceId(getServiceIdFromUrl());
    }
  }, [propServiceId]);

  const handleStarClick = (value) => {
    setSelectedRating(value);
    setError('');
  };

  const handleKeyDown = (e, value) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleStarClick(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!serviceId) {
      setError('Service ID is missing in the URL.');
      return;
    }
    if (selectedRating === 0) {
      setError('Please select a rating.');
      return;
    }
    setSubmitting(true);
    const payload = {
      overall_rating: selectedRating,
      feedback_text: notes.trim(),
    };
    try {
      await submitFeedback(serviceId, payload);
      setThankYou(true);
    } catch (err) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (thankYou) {
    return (
      <div style={styles.pageBg}>
        <div style={styles.card}>
          <img id="logo" src={logo} alt="Company Logo" style={styles.logo} />
          <div style={styles.thankyou}>Thank you for your feedback!</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageBg}>
      <form style={styles.card} autoComplete="off" onSubmit={handleSubmit}>
        <img id="logo" src={logo} alt="Company Logo" style={styles.logo} />
        <h1 id="main-header" style={styles.h1}>Share Your Feedback</h1>
        <div style={styles.stars} role="radiogroup" aria-label="Rating out of 5 stars">
          {[5, 4, 3, 2, 1].map((val) => (
            <button
              type="button"
              key={val}
              style={{
                ...styles.star,
                ...(selectedRating >= val ? styles.starSelected : {}),
              }}
              className={selectedRating >= val ? 'selected' : ''}
              data-value={val}
              role="radio"
              aria-label={`${val} star${val > 1 ? 's' : ''}`}
              aria-checked={selectedRating === val}
              tabIndex={0}
              onClick={() => handleStarClick(val)}
              onKeyDown={(e) => handleKeyDown(e, val)}
            >
              ★
            </button>
          ))}
        </div>
        <label htmlFor="notes" style={styles.label}>Additional Comments (optional):</label>
        <textarea
          id="notes"
          name="notes"
          placeholder="Enter any comments here..."
          style={{
            ...styles.textarea,
            ...(textareaFocus ? styles.textareaFocus : {}),
          }}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onFocus={() => setTextareaFocus(true)}
          onBlur={() => setTextareaFocus(false)}
          disabled={submitting}
        />
        {error && <div style={styles.error}>{error}</div>}
        {submitting && <div style={styles.loading}>Submitting feedback...</div>}
        <button
          type="submit"
          style={{
            ...styles.btn,
            ...(selectedRating === 0 || submitting ? styles.btnDisabled : {}),
          }}
          disabled={selectedRating === 0 || submitting}
        >
          Submit Feedback
        </button>
      </form>
    </div>
  );
};

export default ShareFeedback;